import type React from "react";
import { lowerFirst } from "lodash";
import { useFormAPI } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import { FieldsGrid } from "@/features/fields-grid";
import { useAddFieldTrigger, useRemoveFieldTrigger } from "@/features/form-fields";
import type { RelationInstance } from "@/entities/data-model";
import type { RelationshipAssertion, RelationshipFieldGroup, RelationshipFieldset } from "@/entities/form-metadata";
import type { FieldPath } from "@/shared/forms";
import { usePageContextQuery } from "@/shared/page-context";
import { FieldMapper } from "./FieldMapper/FieldMapper";
import { AddNewFamilyMemberPanel } from "./Panel/AddNewFamilyMemberPanel";
import { HISPANIC_RELATIONSHIP_GRID_LAYOUT, RELATIONSHIP_GRID_LAYOUT } from "./gridLayout";
import styles from "./FamilyMemberGroup.module.scss";

interface FamilyMemberProps {
    fieldGroup: RelationshipFieldGroup;
}

export const FamilyMemberGroup: React.FC<FamilyMemberProps> = ({ fieldGroup }) => {
    const onRemoveItem = useRemoveFieldTrigger();

    const { data: pageContext } = usePageContextQuery();
    const { getFieldValue } = useFormAPI();
    const addNew = useAddFieldTrigger();

    const isMexicoSite = pageContext.partnerId === 5565 && pageContext.locale === "es-MX";
    const gridLayout = isMexicoSite ? HISPANIC_RELATIONSHIP_GRID_LAYOUT : RELATIONSHIP_GRID_LAYOUT;
    const removable = fieldGroup.fieldsetDisplayStrategy !== "prefilled-static";

    const dataAccessorFn = (fieldset: RelationshipFieldset, index: number) =>
        `family.${lowerFirst(fieldset.relation)}.instances.${index}` as FieldPath<FormValues>;

    const formArrayAccessorFn = (fieldset: RelationshipFieldset) =>
        `family.${lowerFirst(fieldset.relation)}.instances` as FieldPath<FormValues>;

    const handleItemAdded = (fieldset: RelationshipFieldset) => {
        const priorityAssertion = fieldGroup.assertions.find(({ assertion }) =>
            fieldset.fields.some((field) => field.assertion === assertion),
        )?.assertion;

        if (priorityAssertion) {
            const relationKey = lowerFirst(fieldset.relation);
            const instancesPath = `family.${relationKey}.instances` as FieldPath<FormValues>;
            const instances = (getFieldValue(instancesPath) as unknown as Array<RelationInstance>) || [];
            const newItemIndex = instances.length;

            addNew({
                fieldName: instancesPath,
                type: "familyMember",
                newItemIndex,
                mappedAssertion: lowerFirst(priorityAssertion),
            });
        }
    };

    const controlsRow = <AddNewFamilyMemberPanel fieldsets={fieldGroup.fieldsets} onItemAdded={handleItemAdded} />;

    return (
        <div className={styles.vm}>
            <FieldsGrid<RelationshipAssertion, RelationshipFieldset, RelationshipFieldGroup>
                fieldGroup={fieldGroup}
                fieldSetToFormArrayPathFn={formArrayAccessorFn}
                fieldDataToFormFieldPathFn={dataAccessorFn}
                onRemoveItem={onRemoveItem}
                FieldMapper={FieldMapper}
                controlsRow={controlsRow}
                gridConfig={{
                    FieldSetName: gridLayout.fieldsetLabel,
                    Assertions: gridLayout.assertions,
                    Removable: removable,
                }}
            />
        </div>
    );
};
